import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { hostKind } from "@/lib/hostKind";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";

/**
 * Three-way host routing guard (mounted once in `RootLayout`).
 *
 * The host taxonomy (`lib/hostKind`) decides which surfaces a host may serve:
 *
 *   - CLUB host (`<slug>.tktic.com`): the full app. No guard — every route is
 *     allowed and the existing per-route auth gates apply.
 *   - ADMIN host (`admin.tktic.com`): operator login-only. Anonymous → /login;
 *     signed-in → /super-admin. There is no marketing landing and no
 *     club-scoped app surface here, so any other path bounces to the operator
 *     surface.
 *   - APEX host (`tktic.com` / `www` / dev-localhost): marketing front door
 *     only. NO login and NO app routes — a login implies a session for *some*
 *     club, and the apex doesn't know which. App/auth paths render the
 *     not-found page (handled by per-route `requireHostKind` guards); this
 *     component additionally keeps the apex from ever showing the login flow.
 *
 * This is ROUTING, not authorization — the server still gates all data. The
 * guard only decides where to send the browser.
 */

/** Paths always allowed on the ADMIN host (the operator surface + auth/account
 * utilities needed to sign in and manage the session).
 *
 * This is an ALLOWLIST: anything absent bounces to the operator surface. A new
 * route that must be reachable here has to be added, or it silently redirects.
 * `/privacy` and `/terms` are listed because legal documents must render on
 * every host — an operator following a footer link would otherwise be bounced
 * to `/super-admin` instead of reading the policy. */
export function isAdminHostAllowed(pathname: string): boolean {
  return (
    pathname === "/login" ||
    pathname === "/privacy" ||
    pathname === "/terms" ||
    pathname.startsWith("/super-admin") ||
    pathname.startsWith("/recovery") ||
    pathname.startsWith("/reset-password") ||
    pathname.startsWith("/verify-email") ||
    pathname.startsWith("/me") ||
    pathname.startsWith("/docs")
  );
}

/** Auth / app-shell paths that imply a club session and so must NOT appear on
 * the marketing apex — HostGuard bounces these to the landing. Genuine
 * club-scoped CONTENT routes (`/teams`, `/match/...`) are intentionally NOT
 * here: they render the not-found page via `RequireClubHost` (a 404, not a
 * silent redirect), which is the desired "apex/teams → 404" behavior.
 *
 * EXCEPTION — the OIDC social-login ceremony, which runs entirely on the APEX
 * (the callback is the fixed apex host and Kratos's continuity cookie is
 * host-scoped, so the whole flow must originate there). Three social markers
 * on apex `/login` are legitimate and must NOT be bounced:
 *   - `?social-start=1&provider=…` — a club-host social click handed the
 *     ceremony here to auto-start the provider flow.
 *   - `?social=1` — our own OIDC-return marker.
 *   - `?id=<flowId>` — Kratos rewrites the query on the OIDC return and drops
 *     `social=1`, landing here with its flow id instead.
 * `KratosLoginPanel` starts/completes the ceremony and then redirects the user
 * to their club host; bouncing any of these to `/` would race+win against the
 * async flow and strand the user on the marketing page. NB: `?flow=` (the
 * re-link deep link — a normal auth form) is NOT a social marker and stays
 * bounced. See KratosLoginPanel / LoginPage + docs/KRATOS.md. */
export function isApexAuthPath(pathname: string, searchStr: string): boolean {
  if (
    pathname === "/login" &&
    /(?:^|[?&])(?:social=1|social-start=1|id=[^&]+)(?:&|$)/.test(searchStr)
  ) {
    return false;
  }
  return (
    pathname === "/login" ||
    pathname === "/hub" ||
    pathname.startsWith("/welcome")
  );
}

export function HostGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  const meQuery = useMe();
  const authMe = useAuthStore((s) => s.me);
  const hydrating = useAuthStore((s) => s.hydrating);
  const redirected = useRef(false);
  const pathname = location.pathname;

  useEffect(() => {
    // Re-arm on every path change so a later in-app navigation to a
    // disallowed path is still corrected.
    redirected.current = false;
  }, [pathname]);

  useEffect(() => {
    if (redirected.current) return;
    if (hydrating || meQuery.isLoading) return;
    const kind = hostKind();
    const signedIn = !!authMe?.user;

    if (kind === "admin") {
      if (isAdminHostAllowed(pathname)) return;
      redirected.current = true;
      if (signedIn) {
        void navigate({ to: "/super-admin", replace: true });
      } else {
        void navigate({ to: "/login", search: { next: "/super-admin" }, replace: true });
      }
      return;
    }

    if (kind === "apex") {
      // The apex is marketing-only: no login, no app shell. Bounce the
      // auth/shell paths to the landing; let genuine club-scoped content
      // routes 404 via their `RequireClubHost` guard.
      if (isApexAuthPath(pathname, location.searchStr)) {
        redirected.current = true;
        void navigate({ to: "/", replace: true });
      }
    }
  }, [pathname, location.searchStr, authMe, hydrating, meQuery.isLoading, navigate]);

  return null;
}
